import { ClipboardList, ArrowRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@tellescope/ui";
import { Button } from "@tellescope/design-system";

interface ResumeIntakeBannerProps {
  /** Name of the medication whose questions are currently in progress. */
  medName: string;
  /** 1-based index of the question the user is on, across all medications. */
  stepCurrent: number;
  /** Total number of intake questions across all medications. */
  stepTotal: number;
  /**
   * Intake is finished but the order hasn't been paid for yet. Switches the banner to a
   * "finish checkout" call-to-action instead of "resume intake".
   */
  complete?: boolean;
  onResume: () => void;
  onDismiss: () => void;
  className?: string;
}

export const ResumeIntakeBanner = ({
  medName,
  stepCurrent,
  stepTotal,
  complete = false,
  onResume,
  onDismiss,
  className,
}: ResumeIntakeBannerProps) => {
  const { t } = useTranslation("shop");
  const pct = complete ? 100 : Math.round((Math.max(stepCurrent - 1, 0) / stepTotal) * 100);

  const heading = complete ? t("Finish your checkout") : t("Pick up where you left off");
  const subline = complete
    ? t("Intake complete · {{medName}} · Ready for payment", { medName })
    : t("Intake in progress · {{medName}} · Question {{stepCurrent}} of {{stepTotal}}", {
        medName,
        stepCurrent,
        stepTotal,
      });
  const ctaLabel = complete ? t("Complete checkout") : t("Resume intake");

  return (
    <div
      data-test="resume-intake-banner"
      className={cn(
        "flex items-center gap-4 px-5 py-3.5 border-b border-primary/20 bg-primary/5 backdrop-blur-2xl",
        className
      )}
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
        <ClipboardList className="w-[18px] h-[18px] text-primary" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground leading-tight">{heading}</p>
        <p className="text-xs text-muted-foreground truncate">{subline}</p>
      </div>

      <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
        <div className="w-24 h-1.5 rounded-full bg-primary/15 overflow-hidden">
          <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground tabular-nums w-8">{pct}%</span>
      </div>

      <Button data-test="resume-intake-button" onClick={onResume} className="flex-shrink-0 gap-1.5 rounded-full">
        {ctaLabel}
        <ArrowRight className="w-3.5 h-3.5" />
      </Button>

      <Button
        variant="ghost"
        iconOnly
        hoverLabel={t("Dismiss")}
        onClick={onDismiss}
        className="flex-shrink-0 rounded-full text-muted-foreground hover:text-foreground hover:bg-primary/10"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
};
